import { useState } from 'react'
import { PencilSquareIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline'
import { showAlert } from 'services/alerts.service'
import { makeRequest } from 'services/request.service'
import TableComponent from 'components/common/TableComponent'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import DeviceGroupModal from 'components/modals/DeviceGroupModal'

const DevicesManagement = ({
  devicesGroups,
  onRefreshDevicesGroups,
  user
}) => {
  const [state, setState] = useState({
    searchTermDeviceGroups: '',
    isOpenDeviceGroupModal: false,
    selectedDeviceGroupToEdit: null
  })

  const { isOpenDeviceGroupModal } = state

  // Helper for auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('secret')}`,
    'Content-Type': 'application/json'
  })

  // PUT/POST devices groups
  const handleCreateDeviceGroup = async data => {
    try {
      const isEditing = !!state?.selectedDeviceGroupToEdit?.deviceGroupId

      const method = isEditing ? 'PUT' : 'POST'
      const url = isEditing
        ? `/management/devices/groups/${state.selectedDeviceGroupToEdit.deviceGroupId}`
        : `/management/devices/groups/${user.farmId}`

      const response = await makeRequest({
        method,
        url,
        data,
        headers: getAuthHeaders(),
        alertResult: true
      })

      if (response.status === 200) {
        handleToggleDeviceGroupModal()
        await onRefreshDevicesGroups()
      }
    } catch (error) {
      showAlert({ message: 'Failed to save device group', icon: 'error' })
      console.error('Error saving device group:', error)
    }
  }

  // Delete device group (bottom table)
  const handleDeleteDeviceGroup = async (deviceGroup) => {
    try {
      const response = await makeRequest({
        method: 'PATCH',
        url: `/management/devices/groups/${deviceGroup.deviceGroupId}`,
        headers: getAuthHeaders(),
        data: {},
        alertResult: true
      })

      if (response.status === 200) {
        await onRefreshDevicesGroups()
        showAlert({ message: 'Device group deleted successfully', icon: 'success' })
      }
    } catch (err) {
      console.error('Error deleting device group:', err)
      showAlert({ message: 'Failed to delete device group', icon: 'error' })
    }
  }

  const handleToggleDeviceGroupModal = (deviceGroup = null) => setState(s => ({
    ...s,
    isOpenDeviceGroupModal: !isOpenDeviceGroupModal,
    selectedDeviceGroupToEdit: deviceGroup
  }))

  // Filter device groups for bottom table
  const filteredDeviceGroups = devicesGroups.filter(group => {
    const searchTerm = state.searchTermDeviceGroups.toLowerCase()
    return group.deviceGroupId.toString().includes(searchTerm) ||
           group.groupName.toLowerCase().includes(searchTerm)
  })

  const devicesTableData = {
    columns: [
      { key: 'deviceGroupId', label: '# Device ID' },
      { key: 'groupName', label: 'Group Name' },
      {
        key: 'actions',
        label: 'Manage',
        cell: (value, row) => (
          <div className='flex items-center gap-2'>
            <button
              title='Edit'
              onClick={() => handleToggleDeviceGroupModal(row)}
              className='p-1 rounded transition'
            >
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDeleteDeviceGroup(row)} title='Delete Device Group' className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: filteredDeviceGroups
  }

  return (
    <>
      {/* Device Groups Management */}
      <ActionCardComponent>
        <input
          type='text'
          placeholder='Search Device Groups...'
          value={state.searchTermDeviceGroups}
          onChange={(e) => setState(s => ({ ...s, searchTermDeviceGroups: e.target.value.toLowerCase() }))}
          className='search-input w-full mt-1 p-2 border rounded-md shadow-sm'
          data-align='left'
        />
        <ButtonComponent onClick={handleToggleDeviceGroupModal}>
          <PlusIcon className='w-5 h-5' />
          New Device Group
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={devicesTableData} defaultSortColumn={devicesTableData.columns[0]?.key} />

      {/* Modals */}
      <DeviceGroupModal
        key={isOpenDeviceGroupModal ? 'open' : 'closed'}
        isOpen={isOpenDeviceGroupModal}
        handleSave={handleCreateDeviceGroup}
        handleClose={handleToggleDeviceGroupModal}
        selectedDevice={state.selectedDeviceGroupToEdit}
      />
    </>
  )
}

export default DevicesManagement
